import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import AnimatedSection from '../../components/common/AnimatedSection';

// 导入AI算法挖矿的子页面
import AiOverview from './AiOverview';
import AiAlgorithm from './AiAlgorithm';
import AiRevenue from './AiRevenue';
import AiTeam from './AiTeam';
import AiPatents from './AiPatents';

// 主容器样式 - 设置基本布局和字体
const AiPageContainer = styled('div', {
  padding: '0',
  margin: '0 auto',
  color: '#333',
  fontFamily: "'Inter', 'Roboto', sans-serif",
});

// 英雄区域样式 - 太空主题背景，包含渐变和点状图案
const HeroSection = styled('div', {
  // 设置背景为ai.jpeg图片，并叠加半透明渐变
  background: `linear-gradient(135deg, rgba(42, 45, 74, 0.7) 0%, rgba(26, 31, 54, 0.7) 100%), url('/images/ai/ai.jpeg') center/cover no-repeat`,
  color: 'white',
  padding: '80px 0',
  position: 'relative',
  overflow: 'hidden',

  // 保留点状图案叠加层
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
    backgroundSize: '30px 30px',
    opacity: 0.5,
    zIndex: 0,
  }
});

// 英雄区域内容容器 - 控制内容布局和响应式设计
const HeroContainer = styled('div', {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  
  '@media (max-width: 992px)': {
    flexDirection: 'column',
    textAlign: 'center',
  },
});

// 英雄区域文字内容 - 控制标题和描述文本的布局
const HeroContent = styled('div', {
  maxWidth: '800px',
  textAlign: 'center',
  
  '@media (max-width: 992px)': {
    paddingRight: '0',
    marginBottom: '40px',
  },
});

// 英雄区域图片相关组件已删除 - 当前未使用

// 英雄区域标签行 - 显示特色标签文本
const HeroTagline = styled('p', {
  fontSize: '18px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: '#FFD700',
  marginBottom: '8px',
});

// 英雄区域标题 - 主标题样式
const HeroTitle = styled('h1', {
  fontSize: '48px',
  fontWeight: '700',
  marginBottom: '20px',
  color: 'white',
  lineHeight: '1.2',

  '@media (max-width: 768px)': {
    fontSize: '36px',
  },
});

// 英雄区域副标题 - 描述文本样式
const HeroSubtitle = styled('p', {
  fontSize: '18px',
  margin: '0 0 30px',
  lineHeight: '1.6',
  color: 'rgba(255, 255, 255, 0.8)',

  '@media (max-width: 768px)': {
    fontSize: '16px',
  },
});

// 英雄区域按钮容器 - 控制按钮布局和响应式显示
const HeroButtonContainer = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  
  '@media (max-width: 992px)': {
    justifyContent: 'center',
  },
  
  '@media (max-width: 576px)': {
    flexDirection: 'column',
    alignItems: 'center',
  },
});

// 英雄区域按钮样式 - 包含主要和轮廓两种按钮样式
const HeroButton = styled('a', {
  display: 'inline-block',
  padding: '14px 32px',
  borderRadius: '4px',
  textDecoration: 'none',
  fontWeight: '600',
  fontSize: '16px',
  transition: 'all 0.3s ease',
  margin: '0 10px 0 0',
  cursor: 'pointer',
  
  '@media (max-width: 576px)': {
    margin: '0 0 15px 0',
    width: '80%',
    textAlign: 'center',
  },
  
  '&.primary': {
    backgroundColor: '#FFD700',
    color: '#333',
    
    '&:hover': {
      backgroundColor: '#f2ca00',
      transform: 'translateY(-2px)',
      boxShadow: '0 5px 15px rgba(255, 215, 0, 0.4)',
    },
  },

  '&.outline': {
    backgroundColor: 'transparent',
    border: '2px solid #FFD700',
    color: '#FFD700',
    
    '&:hover': {
      backgroundColor: 'rgba(255, 215, 0, 0.1)',
      transform: 'translateY(-2px)',
    }
  }
});

// 特性部分容器 - 控制特性展示区域的布局
const FeaturesSection = styled('div', {
  padding: '40px 0 0',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  marginTop: '80px',
});

// 特性内容容器 - 控制特性项目的布局
const FeaturesContainer = styled('div', {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
  gap: '30px',
  
  '@media (max-width: 992px)': {
    gridTemplateColumns: '1fr',
  },
});

// 优化的特性卡片样式 - 参考blog卡片设计
const FeatureCard = styled('div', {
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backgroundColor: 'white',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
  },
});

// 特性卡片图片区域 - 参考blog卡片的图片区域设计
const FeatureCardImage = styled('div', {
  width: '100%',
  height: '120px',
  background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '32px',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 50%)',
  }
});

// 特性卡片内容区域 - 参考blog卡片的内容布局
const FeatureCardContent = styled('div', {
  padding: '25px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

// 特性标题样式 - 优化字体和间距
const FeatureTitle = styled('h3', {
  fontSize: '20px',
  fontWeight: '600',
  marginBottom: '15px',
  color: '#1F2937',
  lineHeight: '1.4',
});

// 特性描述样式 - 优化可读性
const FeatureDescription = styled('p', {
  fontSize: '15px',
  lineHeight: '1.6',
  color: '#4B5563',
  margin: '0 0 20px',
  flex: 1,
});

// 特性指标样式 - 添加醒目的数据展示
const FeatureMetric = styled('div', {
  marginTop: 'auto',
  paddingTop: '15px',
  borderTop: '1px solid #E5E7EB',
  
  '& .metric-value': {
    fontSize: '24px',
    fontWeight: '700',
    color: '#3563E9',
    marginBottom: '5px',
  },
  
  '& .metric-label': {
    fontSize: '13px',
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
});

// 内容区域容器 - 控制主要内容区域的布局
const ContentSection = styled('div', {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '80px 20px',
});

// AI算法挖矿页面组件
const AiPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AiPageContainer>
      <HeroSection>
        <HeroContainer>
          <HeroContent>
            <AnimatedSection direction="up">
              <HeroTagline>{t('ai.tagline', 'BeMine AI mining')}</HeroTagline>
              <HeroTitle>{t('ai.title', 'AI算法助力矿机获得最大收益')}</HeroTitle>
              <HeroSubtitle>
                {t('ai.subtitle', 'BestMine的AI挖矿技术利用人工智能实时分析市场数据、网络难度和设备状态，自动调整挖矿参数以最大化回报。我们的智能系统能优化能源使用，降低成本，同时保持高哈希率，让您的挖矿获得显著提升。')}
              </HeroSubtitle>
              <HeroButtonContainer>
                <HeroButton href="#services" className="primary">{t('ai.goShopping', 'Go shopping')}</HeroButton>
                <HeroButton href="#learn-more" className="outline">{t('ai.learnMore', 'Learn more')}</HeroButton>
              </HeroButtonContainer>
            </AnimatedSection>
          </HeroContent>
        </HeroContainer>
        
        <FeaturesSection>
          <FeaturesContainer>
            <FeatureCard>
              <AnimatedSection direction="up" delay={0.1}>
                <FeatureCardImage>⚡</FeatureCardImage>
                <FeatureCardContent>
                  <FeatureTitle>{t('ai.feature1.title', '平均效率提升')}</FeatureTitle>
                  <FeatureDescription>
                    {t('ai.feature1.description', 'AI算法精调挖矿参数以提升哈希率和效率，确保加密货币挖矿获得高回报。')}
                  </FeatureDescription>
                  <FeatureMetric>
                    <div className="metric-value">30%</div>
                    <div className="metric-label">平均效率提升</div>
                  </FeatureMetric>
                </FeatureCardContent>
              </AnimatedSection>
            </FeatureCard>
            
            <FeatureCard>
              <AnimatedSection direction="up" delay={0.2}>
                <FeatureCardImage>🔋</FeatureCardImage>
                <FeatureCardContent>
                  <FeatureTitle>{t('ai.feature2.title', '能耗降低')}</FeatureTitle>
                  <FeatureDescription>
                    {t('ai.feature2.description', '智能算法优化电力使用，削减运营成本并最小化环境影响')}
                  </FeatureDescription>
                  <FeatureMetric>
                    <div className="metric-value">25%</div>
                    <div className="metric-label">能耗降低</div>
                  </FeatureMetric>
                </FeatureCardContent>
              </AnimatedSection>
            </FeatureCard>
            
            <FeatureCard>
              <AnimatedSection direction="up" delay={0.3}>
                <FeatureCardImage>🎯</FeatureCardImage>
                <FeatureCardContent>
                  <FeatureTitle>{t('ai.feature3.title', '运行稳定性')}</FeatureTitle>
                  <FeatureDescription>
                    {t('ai.feature3.description', 'AI设备的矿机适应市场波动，在任何条件下保持最佳性能')}
                  </FeatureDescription>
                  <FeatureMetric>
                    <div className="metric-value">99.9%</div>
                    <div className="metric-label">运行稳定性</div>
                  </FeatureMetric>
                </FeatureCardContent>
              </AnimatedSection>
            </FeatureCard>
          </FeaturesContainer>
        </FeaturesSection>
      </HeroSection>
      
      <ContentSection>
        {/* AI算法挖矿路由 */}
        <Routes>
          <Route index element={<AiOverview />} />
          <Route path="algorithm" element={<AiAlgorithm />} />
          <Route path="revenue" element={<AiRevenue />} />
          <Route path="team" element={<AiTeam />} />
          <Route path="patents" element={<AiPatents />} />
        </Routes>
      </ContentSection>
    </AiPageContainer>
  );
};

export default AiPage; 